import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { InsertObligationImpactMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

type InsertObligationImpactInput = {
  Description: string;
  ImpactRating: number;
  ParentObligationId: string;
  CustomAttributeData?: CustomAttributeData | null;
};

export const useInsertObligationImpactTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.obligationImpact.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligationImpact.getByParentId.queryKey(),
        });
        // COI-471 parity: Apollo twin also evicts `obligation`.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligation.pathKey(),
        });
      },
    }),
  });

  return {
    insertObligationImpact: async (
      variables: InsertObligationImpactInput
    ): Promise<InsertObligationImpactMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};

function mapTrpcResponseToGraphQL(trpcData: {
  Id: string;
}): InsertObligationImpactMutation {
  return {
    insert_obligation_impact_one: {
      __typename: 'obligation_impact',
      Id: trpcData.Id,
    },
  };
}
